/**
 * EdiService — Pelora Billing agency routes (Phase 22 R1, r1-build-contract.md §5).
 * Component → EdiService → ediClient (apiClient with the billing-edi base URL).
 * Every response is normalised in dtos/billing/edi.dto.ts.
 */

import { ediClient } from '../ediClient';
import type { ApiResponse } from '../../dtos/common/api.dto';
import {
  normalizeAddon,
  normalizeBatch,
  normalizeBatchDetail,
  normalizeBatchList,
  normalizeClaimResponse,
  normalizeCoverageEligibilityList,
  normalizeDashboard,
  normalizeDeadlines,
  normalizeEligibility,
  normalizeEnrollments,
  normalizeSettings,
  normalizeTimeline,
  normalizeWorkItemDetail,
  normalizeWorkItemResponse,
  normalizeWorklist,
  normalizeBatchSendResult,
} from '../../dtos/billing/edi.dto';
import type {
  EdiAddon,
  EdiBatch,
  EdiBatchDetail,
  EdiClaimTimeline,
  EdiCoverageEligibility,
  EdiDashboard,
  EdiDeadlineClaim,
  EdiEligibility,
  EdiEnrollments,
  EdiSettings,
  EdiTimelineClaim,
  EdiWorkActionRequest,
  EdiWorkItem,
  EdiWorkItemDetail,
  EdiWorklistPage,
  EdiBatchSendResult,
  EdiWorklistQuery,
  UpdateEdiSettingsRequest,
} from '../../dtos/billing/edi.dto';

function payload(response: ApiResponse<unknown>, fallback: string): unknown {
  if (response.success) return response.data ?? {};
  throw new Error(response.error?.message || fallback);
}

const enc = encodeURIComponent;

export class EdiService {
  static async getDashboard(): Promise<EdiDashboard> {
    return normalizeDashboard(payload(await ediClient.get('/edi/dashboard'), 'Failed to load the billing dashboard'));
  }

  static async listBatches(): Promise<EdiBatch[]> {
    return normalizeBatchList(payload(await ediClient.get('/edi/batches'), 'Failed to load batches'));
  }

  static async getBatch(batchId: string): Promise<EdiBatchDetail> {
    return normalizeBatchDetail(payload(await ediClient.get(`/edi/batches/${enc(batchId)}`), 'Failed to load the batch'));
  }

  /** Sends the given claims of the batch (all sendable claims when claimIds is omitted). */
  static async sendBatch(batchId: string, claimIds?: string[]): Promise<EdiBatchSendResult> {
    const body = claimIds ? { claimIds } : {};
    return normalizeBatchSendResult(payload(await ediClient.post(`/edi/batches/${enc(batchId)}/send`, body), 'Failed to send the batch'));
  }

  static async holdClaims(batchId: string, claimIds: string[]): Promise<EdiBatch> {
    const data = payload(await ediClient.post(`/edi/batches/${enc(batchId)}/hold`, { claimIds }), 'Failed to hold the claims');
    return normalizeBatch((data as { batch?: unknown }).batch);
  }

  static async getSettings(): Promise<EdiSettings> {
    return normalizeSettings(payload(await ediClient.get('/edi/settings'), 'Failed to load billing settings'));
  }

  static async updateSettings(body: UpdateEdiSettingsRequest): Promise<EdiSettings> {
    return normalizeSettings(payload(await ediClient.put('/edi/settings', body), 'Failed to save billing settings'));
  }

  static async getClaimTimeline(claimId: string): Promise<EdiClaimTimeline> {
    return normalizeTimeline(payload(await ediClient.get(`/edi/claims/${enc(claimId)}/timeline`), 'Failed to load the claim timeline'));
  }

  static async resubmitClaim(claimId: string): Promise<EdiTimelineClaim> {
    return normalizeClaimResponse(payload(await ediClient.post(`/edi/claims/${enc(claimId)}/resubmit`), 'Failed to resubmit the claim'));
  }

  /** Empty filters are not sent. */
  static async listWorklist(query: EdiWorklistQuery = {}): Promise<EdiWorklistPage> {
    const params: Record<string, string> = {};
    (Object.entries(query) as Array<[string, string | undefined]>).forEach(([k, v]) => {
      if (v) params[k] = v;
    });
    return normalizeWorklist(payload(await ediClient.get('/edi/worklist', params), 'Failed to load exceptions'));
  }

  static async getWorkItem(workItemId: string): Promise<EdiWorkItemDetail> {
    return normalizeWorkItemDetail(payload(await ediClient.get(`/edi/worklist/${enc(workItemId)}`), 'Failed to load the exception'));
  }

  static async actOnWorkItem(workItemId: string, request: EdiWorkActionRequest): Promise<EdiWorkItem> {
    const body: EdiWorkActionRequest = { action: request.action };
    if (request.params && Object.keys(request.params).length > 0) body.params = request.params;
    if (request.note && request.note.trim()) body.note = request.note.trim();
    return normalizeWorkItemResponse(
      payload(await ediClient.post(`/edi/worklist/${enc(workItemId)}/actions`, body), 'The action failed')
    );
  }

  static async getEligibility(patientId: string): Promise<EdiCoverageEligibility[]> {
    return normalizeCoverageEligibilityList(payload(await ediClient.get(`/edi/eligibility/${enc(patientId)}`), 'Failed to load eligibility'));
  }

  static async checkEligibility(patientId: string, coverageId: string): Promise<EdiEligibility | null> {
    const data = payload(await ediClient.post(`/edi/eligibility/${enc(patientId)}/check`, { coverageId }), 'The eligibility check failed');
    return normalizeEligibility((data as { eligibility?: unknown }).eligibility);
  }

  static async listEnrollments(): Promise<EdiEnrollments> {
    return normalizeEnrollments(payload(await ediClient.get('/edi/enrollments'), 'Failed to load enrollment status'));
  }

  static async listDeadlines(): Promise<EdiDeadlineClaim[]> {
    return normalizeDeadlines(payload(await ediClient.get('/edi/deadlines'), 'Failed to load filing deadlines'));
  }

  static async getAddon(): Promise<EdiAddon> {
    return normalizeAddon(payload(await ediClient.get('/edi/addon'), 'Failed to load Pelora Billing details'));
  }
}

/** "$0.75" from integer cents. */
export function formatCents(cents: number | null | undefined): string {
  const value = typeof cents === 'number' && Number.isFinite(cents) ? cents : 0;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value / 100);
}

/** "12 claims × $0.75"; null when claims are not charged one by one. */
export function projectedChargeText(claimCount: number, perClaimCents: number | null): string | null {
  if (perClaimCents === null) return null;
  const noun = claimCount === 1 ? 'claim' : 'claims';
  return `${claimCount} ${noun} × ${formatCents(perClaimCents)} = ${formatCents(claimCount * perClaimCents)}`;
}

/** MM/DD/YYYY for a YYYY-MM-DD or ISO date; '—' when empty. */
export function formatEdiDate(value: string | null | undefined): string {
  if (!value) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : value;
}

export default EdiService;
